import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_URL } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ImageCarouselModal } from '@/components/ui/image-carousel-modal';
import { FeedbackModal } from '@/components/ui/feedback-modal';
import { Download, Loader2, Sparkles, Image as ImageIcon, CheckCircle2, XCircle, Clock, MessageSquare } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getIdToken } from '@/lib/firebase';

interface GeneratedImage {
  id: string;
  imageUrl: string;
  width: string;
  height: string;
  imageType: string;
}

interface Generation {
  id: string;
  productName: string;
  originalImageUrl: string;
  style: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  images?: GeneratedImage[];
  expectedImageCount?: number | null;
}

interface Feedback {
  id: string;
  rating: 'satisfied' | 'unsatisfied';
  status: 'pending' | 'reviewed' | 'refunded' | 'rejected';
  requestRefund: string;
  createdAt: string;
}

export function GenerationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [generation, setGeneration] = useState<Generation | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const finalImages = generation?.images?.filter(img => img.imageType === 'final') ?? [];

  useEffect(() => {
    loadGeneration();
    // Poll more frequently while processing to show progressive image reveals
    const interval = setInterval(() => {
      if (generation?.status !== 'completed' && generation?.status !== 'failed') {
        loadGeneration();
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [id, generation?.status]);

  const loadGeneration = async () => {
    try {
      const data = await api.get<Generation>(`/api/generations/${id}`);
      setGeneration(data);
      
      // Load feedback if generation is completed
      if (data.status === 'completed') {
        loadFeedback();
      }
    } catch (error) {
      console.error('Failed to load generation:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFeedback = async () => {
    try {
      const data = await api.get<Feedback>(`/api/generations/${id}/feedback`);
      setFeedback(data);
    } catch (error) {
      // Feedback not found is expected if user hasn't submitted yet
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('Failed to load feedback:', error);
      }
    }
  };

  const getStyleLabel = (style: string) => {
    const labels: Record<string, string> = {
      CONTEXTO_USO: t('newGeneration.styles.CONTEXTO_USO', 'Contexto de Uso'),
      EDITORIAL_LUXO: t('newGeneration.styles.EDITORIAL_LUXO', 'Editorial de Luxo'),
      LIFESTYLE_MINIMAL: t('newGeneration.styles.LIFESTYLE_MINIMAL', 'Lifestyle Minimalista'),
      STUDIO_PROFISSIONAL: t('newGeneration.styles.STUDIO_PROFISSIONAL', 'Estudio Profissional'),
      // Legacy styles -- display-only fallback for history
      FUNDO_BRANCO_MARKETPLACE: 'Fundo Branco (descontinuado)',
      CLOSE_UP_DETALHE: 'Close-up (descontinuado)',
      AMBIENTE_NATURAL: 'Ambiente Natural (descontinuado)',
      FLAT_LAY: 'Flat Lay (descontinuado)',
      MINIMALISTA_SOMBRAS: 'Minimalista (descontinuado)',
    };
    return labels[style] || style;
  };

  const fetchImageBlob = async (imageUrl: string) => {
    try {
      const token = await getIdToken();
      // Use proxy endpoint to bypass CORS
      const proxyUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      
      const response = await fetch(proxyUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
          throw new Error(`Proxy error: ${response.status}`);
      }
      return await response.blob();
    } catch (err) {
      console.warn('Proxy failed, trying direct fetch...', err);
      // Fallback to direct fetch (might fail due to CORS if not configured)
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Direct fetch failed');
      return await response.blob();
    }
  };

  const handleDownloadAll = async () => {
    if (!generation || finalImages.length === 0) return;

    try {
      setIsDownloadingZip(true);
      const zip = new JSZip();

      // Fetch all images concurrently using proxy
      const promises = finalImages.map(async (img, index) => {
        try {
          const blob = await fetchImageBlob(img.imageUrl);
          
          // Determine extension
          let ext = 'jpg';
          if (blob.type === 'image/png') ext = 'png';
          else if (blob.type === 'image/webp') ext = 'webp';
          else if (img.imageUrl.toLowerCase().includes('.png')) ext = 'png';

          const sanitizedName = generation.productName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
          const filename = `${sanitizedName}-${index + 1}.${ext}`;
          
          zip.file(filename, blob);
        } catch (err) {
          console.error('Failed to download image for zip:', img.imageUrl, err);
        }
      });

      await Promise.all(promises);

      // Generate and save zip
      const content = await zip.generateAsync({ type: 'blob' });
      const zipName = `${generation.productName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-images.zip`;
      saveAs(content, zipName);
      
    } catch (error) {
      console.error('Failed to create zip file:', error);
      // Fallback: open each image in a new tab
      generation?.images?.forEach(img => window.open(img.imageUrl, '_blank'));
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleSingleDownload = async (imageUrl: string, index: number) => {
      try {
          const blob = await fetchImageBlob(imageUrl);
          const sanitizedName = generation?.productName.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'image';
          
           let ext = 'jpg';
          if (blob.type === 'image/png') ext = 'png';
          else if (blob.type === 'image/webp') ext = 'webp';

          saveAs(blob, `${sanitizedName}-${index + 1}.${ext}`);
      } catch (error) {
          console.error('Download failed:', error);
          // Fallback: open in new tab
          window.open(imageUrl, '_blank');
      }
  };

  const openCarousel = (index: number) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('generationDetail.loading', 'Loading generation...')}</p>
        </div>
      </div>
    );
  }

  if (!generation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{t('generationDetail.notFound', 'Generation not found')}</p>
            <Button onClick={() => navigate('/')}>{t('generationDetail.backToDashboard', 'Back to Dashboard')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatusIcon = generation.status === 'completed' ? CheckCircle2 : 
                      generation.status === 'processing' ? Loader2 :
                      generation.status === 'failed' ? XCircle : Clock;

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">{generation.productName}</h2>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
              <Badge variant="outline" className="text-xs">{getStyleLabel(generation.style)}</Badge>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">
                {new Date(generation.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
          <Badge
            variant={generation.status === 'completed' ? 'default' :
                    generation.status === 'failed' ? 'destructive' : 'secondary'}
            className="gap-1.5 flex-shrink-0"
          >
            <StatusIcon className={`h-3.5 w-3.5 ${generation.status === 'processing' ? 'animate-spin' : ''}`} />
            {generation.status === 'completed' ? t('dashboard.status.completed', 'Completed') :
             generation.status === 'processing' ? t('dashboard.status.processing', 'Processing') :
             generation.status === 'failed' ? t('dashboard.status.failed', 'Failed') : t('dashboard.status.pending', 'Pending')}
          </Badge>
        </div>
      </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Original Image */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">{t('generationDetail.originalImage', 'Original Image')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={generation.originalImageUrl}
                  alt="Original"
                  className="w-full h-full object-cover"
                />
              </div>
            </CardContent>
          </Card>

          {/* Generated Images */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    {t('generationDetail.generatedImages', 'Generated Images')}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {generation.status === 'completed'
                      ? t('generationDetail.imagesReady', '{{count}} images ready for download', { count: finalImages.length })
                      : generation.status === 'processing'
                      ? t('generationDetail.generating', 'Generating your images with AI...')
                      : generation.status === 'failed'
                      ? t('generationDetail.generationFailed', 'Generation failed')
                      : t('generationDetail.waitingProcessing', 'Waiting for processing')}
                  </CardDescription>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  {/* Feedback Status Badge */}
                  {feedback && (
                    <Badge
                      variant={
                        feedback.status === 'refunded' ? 'default' : 
                        feedback.status === 'rejected' ? 'destructive' : 
                        'secondary'
                      }
                      className="gap-1.5"
                    >
                      {feedback.status === 'refunded' && (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {t('feedback.creditRefunded', 'Créditos reembolsados')}
                        </>
                      )}
                      {feedback.status === 'pending' && feedback.requestRefund === 'true' && (
                        <>
                          <Clock className="h-3.5 w-3.5" />
                          {t('feedback.awaitingReview', 'Aguardando revisão')}
                        </>
                      )}
                      {feedback.status === 'pending' && feedback.requestRefund === 'false' && (
                        <>
                          <MessageSquare className="h-3.5 w-3.5" />
                          {t('feedback.feedbackSent', 'Feedback enviado')}
                        </>
                      )}
                      {feedback.status === 'rejected' && (
                        <>
                          <XCircle className="h-3.5 w-3.5" />
                          {t('feedback.refundRejected', 'Requisição de reembolso rejeitada')}
                        </>
                      )}
                    </Badge>
                  )}
                  
                  {/* Rate Generation Button */}
                  {generation.status === 'completed' && !feedback && (
                    <Button
                      onClick={() => setFeedbackModalOpen(true)}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      {t('feedback.rateGeneration', 'Rate Generation')}
                    </Button>
                  )}
                  
                  {/* Download All Button */}
                  {generation.status === 'completed' && finalImages.length > 0 && (
                    <Button onClick={handleDownloadAll} variant="outline" disabled={isDownloadingZip}>
                      {isDownloadingZip ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                          <Download className="h-4 w-4 mr-2" />
                      )}
                      {t('generationDetail.downloadAll', 'Download All')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {generation.status === 'processing' || generation.status === 'pending' ? (
                (() => {
                  const ready = finalImages;
                  const total = generation.expectedImageCount ?? 3;
                  const pendingCount = Math.max(0, total - ready.length);
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>
                          {ready.length === 0
                            ? 'Gerando suas imagens...'
                            : `${ready.length}/${total} ${ready.length === 1 ? 'imagem pronta' : 'imagens prontas'}`}
                        </span>
                      </div>
                      <div className="grid gap-2 grid-cols-3">
                        {ready.map((img, index) => (
                          <div key={img.id} className="group relative">
                            <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                                 onClick={() => openCarousel(index)}>
                              <img
                                src={img.imageUrl}
                                alt={`Gerada ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  if (!target.dataset.proxied) {
                                    target.dataset.proxied = '1';
                                    target.src = `${API_URL}/api/proxy-image?url=${encodeURIComponent(img.imageUrl)}`;
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        {Array.from({ length: pendingCount }).map((_, i) => (
                          <div key={`pending-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
                            <div className="absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center gap-2">
                              <Loader2 className="h-7 w-7 text-gray-400 animate-spin" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              ) : generation.status === 'failed' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">{t('generationDetail.failedToGenerate', 'Failed to generate images')}</p>
                  <p className="text-sm text-gray-500 mb-4">{t('generationDetail.tryAgainLater', 'Try again later')}</p>
                  <Button onClick={() => navigate('/new')}>{t('nav.newGeneration', 'New Generation')}</Button>
                </div>
              ) : finalImages.length > 0 ? (
                <div className="grid gap-2 grid-cols-3">
                  {finalImages.map((img, index) => (
                    <div key={img.id} className="group relative">
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                           onClick={() => openCarousel(index)}>
                        <img
                          src={img.imageUrl}
                          alt={`Gerada ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (!target.dataset.proxied) {
                              target.dataset.proxied = '1';
                              target.src = `${API_URL}/api/proxy-image?url=${encodeURIComponent(img.imageUrl)}`;
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-1.5"
                        onClick={() => handleSingleDownload(img.imageUrl, index)}
                      >
                        <Download className="h-3.5 w-3.5 md:mr-2" />
                        <span className="hidden md:inline">{t('generationDetail.download', 'Download')}</span>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">{t('generationDetail.noImages', 'No images generated')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Spacer for mobile fixed bottom bar */}
        {generation.status === 'completed' && <div className="h-20 md:hidden" />}

      {/* Image Carousel Modal */}
      {carouselOpen && finalImages.length > 0 && (
        <ImageCarouselModal
          images={finalImages}
          initialIndex={carouselIndex}
          onClose={() => setCarouselOpen(false)}
        />
      )}

      {/* Mobile fixed bottom action bar */}
      {generation.status === 'completed' && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 flex gap-3">
          {!feedback && (
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={() => setFeedbackModalOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
              Avaliar
            </Button>
          )}
          {finalImages.length > 0 && (
            <Button
              className="flex-1 gap-2"
              onClick={handleDownloadAll}
              disabled={isDownloadingZip}
            >
              {isDownloadingZip ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar todas
            </Button>
          )}
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModalOpen && generation && (
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          generationId={generation.id}
          onSubmitSuccess={() => {
            loadFeedback();
            setFeedbackModalOpen(false);
          }}
        />
      )}
    </DashboardLayout>
  );
}
